import { Box, Button, FormControl, Grid, GridItem, Input, Select, Text, Tooltip, VStack } from "@chakra-ui/react";
import { useForm } from "react-hook-form";

import rbtiSchema from "../../../../../data/schemas/rbti.json";

interface RBTIFormData {
  [key: string]: string;
}

interface FieldStatus {
  [key: string]: string;
}

interface Range {
  min: string | number;
  max: string | number;
}

interface RBTIFieldMetadata {
  "optimum-value"?: string | number;
  "healing-range"?: Range;
  "Total Range"?: Range;
  "healing-value"?: string;
}

interface RBTIField {
  name: string;
  fields?: Array<{
    name: string;
    abbreviation: string;
  }>;
  metadata: RBTIFieldMetadata | RBTIFieldMetadata[];
  unit?: string;
  type?: string;
  values?: string[];
  mode?: string;
}

interface RbtiInputFormProps {
  onSubmit: (data: RBTIFormData) => void;
  fieldStatuses: FieldStatus;
  onFieldStatusChange: (fieldName: string, status: string) => void;
}

export const RbtiInputForm = ({ onSubmit, fieldStatuses, onFieldStatusChange }: RbtiInputFormProps) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch
  } = useForm<RBTIFormData>();

  const getMetadataValue = (field: RBTIField, key: keyof RBTIFieldMetadata) => {
    if (Array.isArray(field.metadata)) {
      const item = field.metadata.find((m) => m[key]);
      return item?.[key];
    }
    return field.metadata[key];
  };

  const isRange = (value: unknown): value is Range => {
    return typeof value === "object" && value !== null && "min" in value && "max" in value;
  };

  const isOptimumValue = (value: unknown): value is string | number => {
    return typeof value === "string" || typeof value === "number";
  };

  const formatRange = (range: Range) => {
    if (typeof range.min === "string" && range.min.includes("C")) {
      return `${range.min}-${range.max}`;
    }
    return `${range.min}-${range.max}`;
  };

  const formatOptimumValue = (value: unknown): string => {
    if (value === undefined) return "";
    if (isOptimumValue(value)) return value.toString();
    return "";
  };

  const getHealingValue = (field: RBTIField): string => {
    const healingRange = getMetadataValue(field, "healing-range");
    const healingValue = getMetadataValue(field, "healing-value");

    if (isRange(healingRange)) {
      return formatRange(healingRange);
    }
    if (typeof healingValue === "string") {
      return healingValue;
    }
    return "";
  };

  const getPlaceholder = (field: RBTIField): string => {
    const totalRange = getMetadataValue(field, "Total Range");
    if (field.name === "Urine Cell Debris") {
      return "";
    }
    return isRange(totalRange) ? formatRange(totalRange) : "";
  };

  const handleFieldBlur = (fieldName: string, value: string) => {
    if (value) {
      onFieldStatusChange(fieldName, "Submitted");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, fieldName: string, value: string) => {
    if (e.key === "Enter" && value) {
      onFieldStatusChange(fieldName, "Submitted");
    }
  };

  const renderInputField = (field: RBTIField) => {
    const { name, metadata, unit, type, values } = field;
    const optimumValue = getMetadataValue(field, "optimum-value");
    const fieldValue = watch(name);
    const statusValue = fieldStatuses[name] || "Not Entered";

    return (
      <GridItem key={name}>
        <VStack
          align="start"
          spacing={2}
          p={4}
          borderWidth="1px"
          borderRadius="md"
          minW="160px"
          height="100%"
          minH="280px"
        >
          <Text fontWeight="bold" fontSize="sm">
            {name}
          </Text>

          <FormControl>
            {type === "enum" ? (
              <Select
                {...register(name)}
                size="sm"
                placeholder="Select Value"
                onBlur={(e) => handleFieldBlur(name, e.target.value)}
              >
                <option value="">Select Value</option>
                {values?.map((value: string) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </Select>
            ) : (
              <Input
                {...register(name)}
                placeholder={getPlaceholder(field)}
                size="sm"
                onBlur={(e) => handleFieldBlur(name, e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, name, e.currentTarget.value)}
              />
            )}
          </FormControl>

          <Text fontSize="xs">Optimum: {formatOptimumValue(optimumValue)}</Text>

          <Text fontSize="xs">Healing Range:</Text>
          <Text fontSize="xs">{getHealingValue(field)}</Text>

          <Text fontSize="xs">Status</Text>
          <Select
            {...register(`${name}_status`)}
            size="sm"
            value={statusValue}
            onChange={(e) => onFieldStatusChange(name, e.target.value)}
          >
            <option value="Not Entered">Not Entered</option>
            <option value="Submitted">Submitted</option>
            {type === "enum" &&
              values?.map((value: string) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
          </Select>
        </VStack>
      </GridItem>
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid
        templateColumns={{
          base: "1fr",
          md: `repeat(${rbtiSchema.rbti.input_fields.length}, minmax(160px, 1fr))`
        }}
        gap={4}
        overflowX="auto"
        pb={4}
      >
        {rbtiSchema.rbti.input_fields.map((field) => renderInputField(field))}
      </Grid>

      <Box mt={4}>
        <Tooltip label="Feature coming soon" placement="top" hasArrow>
          <Button
            type="submit"
            borderWidth="1px"
            borderColor="gray.300"
            _hover={{ borderColor: "gray.400" }}
            isDisabled={true}
            cursor="not-allowed"
            opacity={0.7}
          >
            Submit
          </Button>
        </Tooltip>
      </Box>
    </form>
  );
};
